import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import {
  ArrowLeftIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  BriefcaseIcon,
  AcademicCapIcon
} from '@heroicons/react/24/outline';
import { useBrandingColors } from '../hooks/useBrandingColors';
import { getApplicantById as getRelevanterApplicantById } from '../relevanter/services/api';

const ApplicantDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const colors = useBrandingColors();

  const { data: applicant, isLoading, error } = useQuery({
    queryKey: ['applicant', id],
    queryFn: () => getRelevanterApplicantById(id!),
    enabled: !!id,
  });

  const handleBackClick = () => {
    // Попытка закрыть вкладку
    window.close();
    
    // Если вкладка не закрылась (например, она не была открыта через window.open),
    // то переходим назад в истории
    setTimeout(() => {
      navigate(-1);
    }, 100);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    try {
      return format(new Date(dateString), 'dd MMMM yyyy', { locale: ru });
    } catch {
      return dateString;
    }
  };

  const formatExperience = (start: string, end?: string) => {
    if (!start) return '';
    const startDate = formatDate(start);
    const endDate = end ? formatDate(end) : 'Настоящее время';
    return `${startDate} - ${endDate}`;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div 
            className="inline-block animate-spin rounded-full h-12 w-12 border-b-2"
            style={{
              borderTopColor: 'transparent',
              borderRightColor: colors.primary,
              borderBottomColor: colors.primary,
              borderLeftColor: colors.primary
            }}
          ></div>
          <p className="mt-2 text-gray-600">Загрузка кандидата...</p>
        </div>
      </div>
    );
  }

  if (error) {
    const is404 = (error as any)?.response?.status === 404 || (error as any)?.message?.includes('не найден');
    
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-7xl mx-auto">
          <div className={`${is404 ? 'bg-yellow-50 border-yellow-200' : 'bg-red-50 border-red-200'} border rounded-lg p-4`}>
            <p className={`${is404 ? 'text-yellow-800' : 'text-red-800'}`}>
              {is404 ? 'Кандидат не найден' : `Ошибка загрузки данных кандидата: ${(error as any)?.message}`}
            </p>
          </div>
          <button
            onClick={handleBackClick}
            className="mt-4 flex items-center gap-2 text-gray-600 hover:text-gray-700"
            style={{ color: colors.primary }}
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Вернуться к поиску
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#e9eae2] relative h-full overflow-hidden">
      <div className="relative h-full">
        <div className="box-border content-start flex gap-4 items-stretch justify-start p-0 relative w-full h-full">
          {/* Sidebar - слева на десктопе */}
          <div className="bg-[#f5f6f1] rounded-[24px] p-[20px] shrink-0 w-[460px] h-full overflow-hidden">
            {/* Info Card */}
            <div className="bg-[#ffffff] rounded-3xl p-[24px] mb-4">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Информация</h2>
              
                <dl className="space-y-3">
                {applicant?.age && (
                  <div>
                    <dt className="text-sm text-gray-500">Возраст</dt>
                    <dd className="text-gray-900">{applicant.age} лет</dd>
                  </div>
                )}
                
                {applicant?.gender && (
                  <div>
                    <dt className="text-sm text-gray-500">Пол</dt>
                    <dd className="text-gray-900">
                      {applicant.gender === 'male' ? 'Мужской' : 'Женский'}
                    </dd>
                  </div>
                )}
                
                {applicant?.educationLevel && (
                  <div>
                    <dt className="text-sm text-gray-500">Уровень образования</dt>
                    <dd className="text-gray-900">{applicant.educationLevel.name}</dd>
                  </div>
                )}
                
                {applicant?.totalExperienceMonths && (
                  <div>
                    <dt className="text-sm text-gray-500">Общий опыт</dt>
                    <dd className="text-gray-900">
                      {Math.floor(applicant.totalExperienceMonths / 12)} лет
                    </dd>
                  </div>
                )}
                
                {applicant?.languages && applicant.languages.length > 0 && (
                  <div>
                    <dt className="text-sm text-gray-500">Языки</dt>
                    <dd className="text-gray-900">
                      {applicant.languages.map((lang: any) => lang.name).join(', ')}
                    </dd>
                  </div>
                )}
                
                {applicant?.citizenships && applicant.citizenships.length > 0 && (
                  <div>
                    <dt className="text-sm text-gray-500">Гражданство</dt>
                    <dd className="text-gray-900">
                      {applicant.citizenships.map((c: any) => c.name).join(', ')}
                    </dd>
                  </div>
                )}
                </dl>
              </div>

              {/* Status History */}
              {applicant?.statusHistory && applicant.statusHistory.length > 0 && (
                <div className="bg-[#ffffff] rounded-3xl p-[24px]">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">История статусов</h2>
                  <div className="space-y-2">
                    {applicant.statusHistory
                      .sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime())
                      .map((status: any, index: number) => (
                        <div key={index} className="flex items-center justify-between py-2 border-b last:border-0">
                          <span className="text-sm font-medium text-gray-900">
                            {status.status?.name || 'Статус'}
                          </span>
                          <span className="text-sm text-gray-500">
                            {formatDate(status.date)}
                          </span>
                        </div>
                      ))}
                  </div>
                </div>
              )}
          </div>

          {/* Main Content - правая колонка как на странице вакансий */}
          <div className="box-border content-stretch flex flex-col gap-4 items-stretch justify-start p-0 relative shrink-0 w-[1110px] flex-1 h-full max-h-full overflow-y-auto">
            {/* Header Block container */}
            <div className="bg-[#f5f6f1] rounded-[24px] p-[20px]">
              {/* Header Card */}
              <div className="bg-[#ffffff] rounded-3xl p-[24px]">
                {/* Заголовок с кнопкой на одном уровне */}
                <div className="flex justify-between items-start mb-2">
                  <h1 className="text-3xl font-bold text-gray-900">
                {applicant?.fullName || applicant?.name || 
                 [applicant?.lastName, applicant?.firstName, applicant?.middleName]
                   .filter(Boolean)
                   .join(' ') || 
                 'Имя не указано'
                }
                  </h1>
                  
                  <button
                    onClick={handleBackClick}
                    className="flex items-center gap-2 text-gray-600 hover:text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors"
                    style={{ color: colors.primary }}
                  >
                    <ArrowLeftIcon className="h-4 w-4" />
                    Вернуться к поиску
                  </button>
                </div>
              
              <p className="text-xl text-gray-600 mb-4">
                {applicant?.position}
              </p>

              <div className="flex flex-wrap gap-4 text-gray-600">
                {/* Parse contacts for HH.ru or use direct fields for e-staff */}
                {(() => {
                  let email = applicant?.email;
                  let phone = applicant?.phone;
                  
                  // If HH.ru format with contact array, parse it
                  if (applicant?.contact && Array.isArray(applicant.contact)) {
                    applicant.contact.forEach((contact: any) => {
                      if (contact.type?.id === 'email') {
                        email = contact.value;
                      } else if (contact.type?.id === 'cell' || contact.type?.id === 'phone') {
                        phone = contact.value?.formatted || contact.value;
                      }
                    });
                  }
                  
                  return (
                    <>
                      {email && (
                        <div className="flex items-center gap-2">
                          <EnvelopeIcon className="h-5 w-5" />
                          <span>{email}</span>
                        </div>
                      )}
                      
                      {phone && (
                        <div className="flex items-center gap-2">
                          <PhoneIcon className="h-5 w-5" />
                          <span>{phone}</span>
                        </div>
                      )}
                    </>
                  );
                })()}
                
                {/* Show contact comment/note if available */}
                {applicant?.contact && Array.isArray(applicant.contact) && 
                 applicant.contact.find((c: any) => c.comment) && (
                  <div className="flex items-center gap-2 text-sm text-blue-600">
                    <span>📝 {applicant.contact.find((c: any) => c.comment)?.comment}</span>
                  </div>
                )}
                
                {applicant?.area && (
                  <div className="flex items-center gap-2">
                    <MapPinIcon className="h-5 w-5" />
                    <span>{applicant.area.name}</span>
                  </div>
                )}
              </div>

              {applicant?.salary && (
                <div className="mt-4 p-3 bg-gray-50 rounded-lg inline-block">
                  <span className="text-sm text-gray-600">Желаемая зарплата: </span>
                  <span className="font-semibold text-gray-900">
                    {applicant.salary.amount.toLocaleString()} {applicant.salary.currency}
                  </span>
                </div>
              )}
              </div>
            </div>

            {/* Skills */}
            {(applicant?.skills || applicant?.skillsets) && (
              <div className="bg-[#f5f6f1] rounded-[24px] p-[20px]">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Навыки</h2>
                <div className="flex flex-wrap gap-2">
                  {(() => {
                    let skillsArray: string[] = [];
                    
                    // For E-Staff: try skillsets first, fallback to skills
                    if (applicant?.skillsets && Array.isArray(applicant.skillsets) && applicant.skillsets.length > 0) {
                      // E-Staff format - already an array of strings
                      skillsArray = applicant.skillsets;
                    } else if (applicant?.skills) {
                      // Fallback to skills field (works for both E-Staff and HH.ru)
                      skillsArray = Array.isArray(applicant.skills) 
                        ? applicant.skills 
                        : applicant.skills.split(',');
                    }
                    
                    return skillsArray.map((skill, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 rounded-full text-sm"
                        style={{
                          backgroundColor: `${colors.primary}20`,
                          color: colors.primary
                        }}
                      >
                        {typeof skill === 'string' ? skill.trim() : skill}
                      </span>
                    ));
                  })()}
                </div>
              </div>
            )}

            {/* About */}
            {applicant?.body && (
              <div className="bg-[#f5f6f1] rounded-[24px] p-[20px]">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">О себе</h2>
                <p className="text-gray-600 whitespace-pre-wrap">{applicant.body}</p>
              </div>
            )}

            {/* Experience */}
            {((applicant?.experiences && applicant.experiences.length > 0) || 
              (applicant?.experience && applicant.experience.length > 0)) && (
              <div className="bg-[#f5f6f1] rounded-[24px] p-[20px]">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <BriefcaseIcon className="h-5 w-5" />
                  Опыт работы
                </h2>
                <div className="space-y-4">
                  {(applicant?.experiences || applicant?.experience || []).map((exp: any, index: number) => (
                    <div key={index} className="border-l-2 pl-4" style={{ borderColor: colors.primary }}>
                      <h3 className="font-semibold text-gray-900">{exp.position}</h3>
                      <p className="text-gray-600">{exp.company}</p>
                      <p className="text-sm text-gray-500 mt-1">
                        {formatExperience(exp.start, exp.end)}
                      </p>
                      {exp.description && (
                        <p className="text-gray-600 mt-2">{exp.description}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Education */}
            {((applicant?.educations && applicant.educations.length > 0) || 
              (applicant?.education?.primary && applicant.education.primary.length > 0)) && (
              <div className="bg-[#f5f6f1] rounded-[24px] p-[20px]">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <AcademicCapIcon className="h-5 w-5" />
                  Образование
                </h2>
                <div className="space-y-3">
                  {(applicant?.educations || applicant?.education?.primary || []).map((edu: any, index: number) => (
                    <div key={index} className="border-l-2 pl-4" style={{ borderColor: colors.primary }}>
                      <h3 className="font-semibold text-gray-900">{edu.name}</h3>
                      {edu.organization && <p className="text-gray-600">{edu.organization}</p>}
                      {edu.result && <p className="text-gray-600">{edu.result}</p>}
                      {edu.year && <p className="text-sm text-gray-500">Год окончания: {edu.year}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplicantDetailPage;
